import { db, DexieTemplate, DexieSignedContract, DexieProfile } from './dexieDb';
import { supabase } from '@/integrations/supabase/client';

// ============================================
// TYPES & HELPERS
// ============================================

export type SyncResult =
  | { success: true; pushedCount: number }
  | { success: false; error: string };

const RETRY_DELAYS = [500, 1000, 2000];

async function withRetry(
  fn: () => Promise<{ error: any }>,
  maxAttempts = 3
): Promise<{ error: any }> {
  let lastError: any = null;
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    const result = await fn();
    if (!result.error) return result;
    lastError = result.error;
    // Ne pas retry sur erreur métier 4xx
    const status = result.error?.status ?? result.error?.code;
    if (typeof status === 'number' && status >= 400 && status < 500) {
      return result;
    }
    if (attempt < maxAttempts - 1) {
      await new Promise(r => setTimeout(r, RETRY_DELAYS[attempt] ?? 2000));
    }
  }
  return { error: lastError };
}

// ============================================
// PULL: Cloud → Dexie  (runs when online)
// ============================================

export async function pullProfiles(userId: string) {
  const { data, error } = await supabase
    .from('profiles')
    .select('*')
    .eq('user_id', userId)
    .single();
  if (error || !data) return;

  await db.profiles.put({
    user_id: data.user_id,
    display_name: data.display_name,
    business_name: data.business_name,
    email: data.email,
    default_language: data.default_language,
    pin_code: data.pin_code,
    pin_enabled: data.pin_enabled,
    logo_url: (data as any).logo_url ?? null,
    _syncStatus: 'synced',
    _lastModified: Date.now(),
  });
}

export async function pullTemplates(userId: string) {
  const { data, error } = await supabase
    .from('templates')
    .select('*')
    .eq('owner_id', userId)
    .order('created_at', { ascending: false });
  if (error || !data) return;

  // Get locally pending items so we don't overwrite them
  const pending = await db.templates
    .where('_syncStatus')
    .anyOf(['pending_create', 'pending_update', 'pending_delete'])
    .toArray();
  const pendingIds = new Set(pending.map(p => p.id));

  const rows: DexieTemplate[] = data
    .filter(row => !pendingIds.has(row.id))
    .map(row => ({
      id: row.id,
      owner_id: row.owner_id,
      name: row.name as any,
      category: row.category,
      description: row.description as any,
      icon: row.icon,
      sections: row.sections as any,
      fields: row.fields as any,
      checkboxes: row.checkboxes as any,
      tags: row.tags || [],
      archived: row.archived,
      is_system: (row as any).is_system ?? false,
      created_at: row.created_at,
      updated_at: row.updated_at,
      _syncStatus: 'synced' as const,
      _lastModified: Date.now(),
    }));

  await db.templates.bulkPut(rows);

  // Remove local templates that no longer exist in cloud (and aren't pending)
  const cloudIds = new Set(data.map(d => d.id));
  const allLocal = await db.templates.where('owner_id').equals(userId).toArray();
  const toDelete = allLocal.filter(t => !cloudIds.has(t.id) && t._syncStatus === 'synced');
  if (toDelete.length > 0) {
    await db.templates.bulkDelete(toDelete.map(t => t.id));
  }
}

export async function pullSignedContracts(userId: string) {
  const { data, error } = await supabase
    .from('signed_contracts')
    .select('*')
    .eq('owner_id', userId)
    .order('signed_at', { ascending: false });
  if (error || !data) return;

  const pending = await db.signedContracts
    .where('_syncStatus')
    .anyOf(['pending_create', 'pending_delete'])
    .toArray();
  const pendingIds = new Set(pending.map(p => p.id));

  const rows: DexieSignedContract[] = data
    .filter(row => !pendingIds.has(row.id))
    .map(row => ({
      id: row.id,
      owner_id: row.owner_id,
      template_id: row.template_id,
      template_name: row.template_name,
      client_id: row.client_id || null,
      client_name: row.client_name,
      client_email: row.client_email,
      language: row.language,
      field_values: row.field_values as any,
      checkbox_values: row.checkbox_values as any,
      signature_data_url: row.signature_data_url,
      signed_at: row.signed_at,
      practitioner_email: row.practitioner_email,
      _syncStatus: 'synced' as const,
      _lastModified: Date.now(),
    }));

  await db.signedContracts.bulkPut(rows);
}

// ============================================
// PUSH: Dexie → Cloud  (runs when online)
// ============================================

export async function pushPendingTemplates(userId: string) {
  const pending = await db.templates
    .where('_syncStatus').anyOf(['pending_create', 'pending_update'])
    .and(t => t.owner_id === userId)
    .toArray();
  for (const t of pending) {
    const { _syncStatus, _lastModified, ...row } = t;
    const { error } = await withRetry(async () => supabase.from('templates').upsert(row, { onConflict: 'id', ignoreDuplicates: true }));
    if (!error) {
      await db.templates.update(t.id, { _syncStatus: 'synced' });
    }
  }

  const deletes = await db.templates
    .where('_syncStatus').equals('pending_delete')
    .and(t => t.owner_id === userId)
    .toArray();
  for (const t of deletes) {
    const { error } = await withRetry(async () =>
      supabase.from('templates').delete().eq('id', t.id).eq('owner_id', userId)
    );
    if (!error) {
      await db.templates.delete(t.id);
    }
  }
}

export async function pushPendingSignedContracts(userId: string) {
  const creates = await db.signedContracts
    .where('_syncStatus').equals('pending_create')
    .and(c => c.owner_id === userId)
    .toArray();
  for (const c of creates) {
    const { _syncStatus, _lastModified, ...row } = c;
    const { error } = await withRetry(async () => supabase.from('signed_contracts').upsert(row));
    if (!error) {
      await db.signedContracts.update(c.id, { _syncStatus: 'synced' });
    }
  }

  const deletes = await db.signedContracts
    .where('_syncStatus').equals('pending_delete')
    .and(c => c.owner_id === userId)
    .toArray();
  for (const d of deletes) {
    const { error } = await withRetry(async () =>
      supabase.from('signed_contracts').delete().eq('id', d.id).eq('owner_id', userId)
    );
    if (!error) {
      await db.signedContracts.delete(d.id);
    }
  }
}

export async function pushPendingProfile(userId: string) {
  const profile = await db.profiles.get(userId);
  if (!profile || profile._syncStatus === 'synced') return;

  const { _syncStatus, _lastModified, ...updates } = profile;
  const { error } = await withRetry(async () =>
    supabase.from('profiles').update({
      display_name: updates.display_name,
      business_name: updates.business_name,
      email: updates.email,
      default_language: updates.default_language,
      pin_code: updates.pin_code,
      pin_enabled: updates.pin_enabled,
      logo_url: updates.logo_url,
      first_name: updates.first_name,
      last_name: updates.last_name,
      phone: updates.phone,
      instagram: updates.instagram,
      business_address: updates.business_address,
    }).eq('user_id', userId)
  );
  if (!error) {
    await db.profiles.update(userId, { _syncStatus: 'synced' });
  }
}

// ============================================
// FULL SYNC
// ============================================

export async function syncAll(userId: string): Promise<SyncResult> {
  if (!navigator.onLine) return { success: false, error: 'offline' };

  try {
    // Vérifier session auth avant tout push
    const { data: { session } } = await supabase.auth.getSession();
    if (!session) return { success: false, error: 'session_expired' };

    let pushedCount = 0;

    // Push first (local changes take priority)
    const profileBefore = await db.profiles.get(userId);
    await pushPendingProfile(userId);
    const profileAfter = await db.profiles.get(userId);
    if (profileBefore?._syncStatus !== 'synced' && profileAfter?._syncStatus === 'synced') {
      pushedCount++;
    }

    const templatesBefore = await db.templates
      .where('_syncStatus').anyOf(['pending_create', 'pending_update', 'pending_delete'])
      .and(t => t.owner_id === userId).count();
    await pushPendingTemplates(userId);
    const templatesAfter = await db.templates
      .where('_syncStatus').anyOf(['pending_create', 'pending_update', 'pending_delete'])
      .and(t => t.owner_id === userId).count();
    pushedCount += templatesBefore - templatesAfter;

    const contractsBefore = await db.signedContracts
      .where('_syncStatus').anyOf(['pending_create', 'pending_delete'])
      .and(c => c.owner_id === userId).count();
    await pushPendingSignedContracts(userId);
    const contractsAfter = await db.signedContracts
      .where('_syncStatus').anyOf(['pending_create', 'pending_delete'])
      .and(c => c.owner_id === userId).count();
    pushedCount += contractsBefore - contractsAfter;

    // Then pull latest from cloud
    await pullProfiles(userId);
    await pullTemplates(userId);
    await pullSignedContracts(userId);

    return { success: true, pushedCount: Math.max(0, pushedCount) };
  } catch (e: any) {
    return { success: false, error: e?.message ?? 'unknown_error' };
  }
}
